import { cn } from "./classNames-DcGnTh2N.js";
import { useArgoDesign } from "./context-U293r3v-.js";
import { Check, ChevronRight, Circle, Loader2, ToggleRight } from "./icons-ZIeZXXS7.js";
import { radioCircleVariants, radioIndicatorVariants } from "./styles-BDA5rQz3.js";
import { ScrollArea } from "./internal/components/ScrollArea.js";
import { createContext, use, useMemo } from "react";
import { Root, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Group, Root as Root$1, Trigger } from "@radix-ui/react-dropdown-menu";
//#region src/components/DropdownMenu/DropdownMenuContext.tsx
const DropdownMenuContext = createContext({ variant: "default" });
const useDropdownMenuContext = () => use(DropdownMenuContext);
//#endregion
//#region src/components/DropdownMenu/DropdownMenuItemIndicator.tsx
const variants = cva([
	"tailwindcss-preflight",
	"tw:pointer-events-none",
	"tw:size-4",
	"tw:aspect-square",
	"tw:shrink-0",
	"tw:flex",
	"tw:items-center",
	"tw:justify-center"
], { variants: { variant: {
	none: [],
	radio: ["tw:data-[state=unchecked]:[&_svg]:hidden"],
	check: ["tw:[&_svg:not([class*=\"tw:size-\"])]:size-3.5", "tw:[&_svg]:text-primary"],
	checkbox: [
		"tw:[&_svg]:text-primary-foreground",
		"tw:border",
		"tw:border-solid",
		"tw:rounded",
		"tw:group-data-[variant=default]/dropdown-menu-content:border-input",
		"tw:group-data-[variant=argo]/dropdown-menu-content:border-input",
		"tw:[&_svg:not([class*=\"tw:size-\"])]:size-3",
		"tw:data-[state=unchecked]:[&_svg]:hidden",
		"tw:group-data-[variant=argo]/dropdown-menu-content:data-[state=checked]:border-primary",
		"tw:group-data-[variant=argo]/dropdown-menu-content:data-[state=checked]:bg-primary",
		"tw:group-data-[variant=default]/dropdown-menu-content:data-[state=checked]:border-primary",
		"tw:group-data-[variant=default]/dropdown-menu-content:data-[state=checked]:bg-primary"
	],
	switch: [
		"tw:size-5",
		"tw:[&_svg:not([class*=\"tw:size-\"])]:size-5",
		"tw:text-muted-foreground",
		"tw:data-[state=unchecked]:rotate-180",
		"tw:group-data-[variant=default]/dropdown-menu-content:data-[state=checked]:text-primary",
		"tw:group-data-[variant=argo]/dropdown-menu-content:data-[state=checked]:text-primary"
	]
} } });
const DropdownMenuItemIndicator = ({ variant, className, forceMount: forceMountProp, ...rest }) => {
	const { variant: menuVariant } = useDropdownMenuContext();
	const isRadio = variant === "radio";
	const IconComp = (() => {
		if (variant === "switch") return ToggleRight;
		if (isRadio) return Circle;
		return Check;
	})();
	const forceMount = (() => {
		if (forceMountProp) return true;
		return variant === "check" ? void 0 : true;
	})();
	if (variant === "none") return null;
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, {
		...rest,
		forceMount,
		className: cn(variants({ variant }), isRadio && radioCircleVariants({ variant: menuVariant }), className),
		children: /* @__PURE__ */ jsx(IconComp, { className: isRadio ? radioIndicatorVariants({ variant: menuVariant }) : void 0 })
	});
};
//#endregion
//#region src/components/DropdownMenu/ItemIcon.tsx
const ItemIcon = ({ className, children, ...props }) => /* @__PURE__ */ jsx("span", {
	...props,
	className: cn("tw:inline-flex", "tw:items-center", "tw:justify-center", "tw:aspect-square", "tw:size-5", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-4", "tw:group-data-[variant=default]/dropdown-menu-content:text-muted-foreground", "tw:group-data-[variant=argo]/dropdown-menu-content:text-primary", "tw:group-data-[variant=argo]/dropdown-menu-content:group-aria-disabled:text-muted-foreground", "tw:group-data-[variant=default]/dropdown-menu-content:group-hover:text-primary", className),
	children
});
//#endregion
//#region src/components/DropdownMenu/styles.ts
const itemVariants = cn("tailwindcss-preflight", "tw:outline-hidden", "tw:group", "tw:peer", "tw:relative", "tw:flex", "tw:flex-1", "tw:items-center", "tw:gap-2", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-4", "tw:cursor-pointer", "tw:select-none", "tw:transition-colors", "tw:p-2", "tw:font-sans", "tw:text-sm", "tw:aria-disabled:pointer-events-none", "tw:aria-disabled:opacity-disabled", "tw:focus-visible:outline-hidden", "tw:group-data-[variant=default]/dropdown-menu-content:rounded", "tw:group-data-[variant=default]/dropdown-menu-content:focus:bg-muted", "tw:group-data-[variant=default]/dropdown-menu-content:focus:text-foreground", "tw:group-data-[variant=default]/dropdown-menu-content:hover:bg-muted", "tw:group-data-[variant=default]/dropdown-menu-content:hover:text-foreground", "tw:group-data-[variant=argo]/dropdown-menu-content:rounded-md", "tw:group-data-[variant=argo]/dropdown-menu-content:text-popover-foreground", "tw:group-data-[variant=argo]/dropdown-menu-content:focus:text-[var(--hover-foreground,rgb(var(--primary)))]", "tw:group-data-[variant=argo]/dropdown-menu-content:focus:bg-[var(--hover,color-mix(in_oklab,rgb(var(--primary))_10%,transparent))]", "tw:group-data-[variant=argo]/dropdown-menu-content:hover:text-[var(--hover-foreground,rgb(var(--primary)))]", "tw:group-data-[variant=argo]/dropdown-menu-content:hover:bg-[var(--hover,color-mix(in_oklab,rgb(var(--primary))_10%,transparent))]");
const contentVariants = cn("tailwindcss-preflight", "tw:group/dropdown-menu-content", "tw:z-[1055]", "tw:flex", "tw:flex-col", "tw:origin-(--radix-dropdown-menu-content-transform-origin)", "tw:max-h-[var(--radix-dropdown-menu-content-available-height)]", "tw:min-w-32", "tw:border", "tw:border-border", "tw:shadow-md", "tw:bg-popover", "tw:text-popover-foreground", "tw:data-[variant=default]:rounded-md", "tw:data-[variant=argo]:rounded-popover", "tw:data-[state=open]:animate-in", "tw:data-[state=open]:fade-in-0", "tw:data-[state=open]:zoom-in-95", "tw:data-[state=closed]:animate-out", "tw:data-[state=closed]:fade-out-0", "tw:data-[state=closed]:zoom-out-95", "tw:data-[side=bottom]:slide-in-from-top-2", "tw:data-[side=left]:slide-in-from-right-2", "tw:data-[side=right]:slide-in-from-left-2", "tw:data-[side=top]:slide-in-from-bottom-2");
//#endregion
//#region src/components/DropdownMenu/DropdownMenuCheckboxItem.tsx
const DropdownMenuCheckboxItem = ({ className, children, checked, closeOnSelect = false, onSelect, indicatorAlign = "start", indicator = "checkbox", isLoading = false, icon, ...props }) => {
	const isAtStart = indicatorAlign === "start";
	const indicatorEle = isLoading ? /* @__PURE__ */ jsx(ItemIcon, {
		className: isAtStart ? "" : "tw:ml-auto",
		children: /* @__PURE__ */ jsx(Loader2, { className: "tw:animate-spin" })
	}) : /* @__PURE__ */ jsx(DropdownMenuItemIndicator, {
		variant: indicator,
		className: isAtStart ? "" : "tw:ml-auto"
	});
	const handleSelect = (event) => {
		if (!closeOnSelect) event.preventDefault();
		onSelect?.(event);
	};
	return /* @__PURE__ */ jsxs(DropdownMenuPrimitive.CheckboxItem, {
		className: cn(itemVariants, className),
		checked,
		onSelect: handleSelect,
		...props,
		children: [
			isAtStart && indicatorEle,
			icon && /* @__PURE__ */ jsx(ItemIcon, { children: icon }),
			children,
			!isAtStart && indicatorEle
		]
	});
};
//#endregion
//#region src/components/DropdownMenu/DropdownMenuContent.tsx
const DropdownMenuContent = ({ className, sideOffset = 4, forceMount, children, viewPortClassName, container, ...props }) => {
	const { variant } = useDropdownMenuContext();
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, {
		forceMount: forceMount || void 0,
		container,
		children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.Content, {
			...props,
			"data-variant": variant,
			sideOffset,
			className: contentVariants,
			children: /* @__PURE__ */ jsx(ScrollArea, {
				className,
				viewPortClassName: cn("tw:p-2", "tw:max-h-[var(--radix-dropdown-menu-content-available-height)]", viewPortClassName),
				scrollBarClassName: "tw:w-1.5",
				children
			})
		})
	});
};
//#endregion
//#region src/components/DropdownMenu/DropdownMenuItem.tsx
const DropdownMenuItem = ({ ref, className, asModalTrigger = false, onSelect, onPointerLeave, onPointerMove, children, asChild, icon, iconClassName, isLoading = false, ...props }) => {
	const Comp = asChild ? Root : "div";
	const adornment = isLoading ? /* @__PURE__ */ jsx(Loader2, { className: "tw:animate-spin" }) : icon;
	const handleOnSelect = (event) => {
		if (asModalTrigger) event.preventDefault();
		onSelect?.(event);
	};
	const handlePointerLeave = (event) => {
		if (asModalTrigger) event.preventDefault();
		onPointerLeave?.(event);
	};
	const handlePointerMove = (event) => {
		if (asModalTrigger) event.preventDefault();
		onPointerMove?.(event);
	};
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Item, {
		...props,
		ref,
		onSelect: handleOnSelect,
		onPointerLeave: handlePointerLeave,
		onPointerMove: handlePointerMove,
		className: cn(itemVariants, asChild && "tw:text-inherit", className),
		asChild: true,
		children: /* @__PURE__ */ jsxs(Comp, { children: [adornment && /* @__PURE__ */ jsx(ItemIcon, {
			className: iconClassName,
			children: adornment
		}), /* @__PURE__ */ jsx(Slottable, { children })] })
	});
};
//#endregion
//#region src/components/DropdownMenu/DropdownMenuItemDescription.tsx
const descriptionVariants = cva([
	"tw:box-border",
	"tw:ml-auto",
	"tw:font-sans",
	"tw:tracking-normal",
	"tw:font-normal",
	"tw:text-xs",
	"tw:text-muted-foreground"
], {
	variants: { hideOnHover: {
		true: ["tw:group-focus-within:hidden", "tw:group-hover:hidden"],
		false: []
	} },
	defaultVariants: {}
});
const DropdownMenuItemDescription = ({ className, children, hideOnHover = false, ...props }) => {
	return /* @__PURE__ */ jsx("span", {
		...props,
		className: cn(descriptionVariants({ hideOnHover }), className),
		children
	});
};
//#endregion
//#region src/components/DropdownMenu/DropdownMenuLabel.tsx
const DropdownMenuLabel = ({ className, ...props }) => {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Label, {
		className: cn("tw:p-2", "tw:font-medium", "tw:text-xs", "tw:text-muted-foreground", className),
		...props
	});
};
//#endregion
//#region src/components/DropdownMenu/DropdownMenuRadioGroup/DropdownMenuRadioGroup.tsx
const DropdownMenuRadioGroup = ({ value, defaultValue, onValueChange, ...props }) => {
	const handleChange = (value) => {
		onValueChange?.(value);
	};
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.RadioGroup, {
		...props,
		value,
		defaultValue,
		onValueChange: handleChange
	});
};
//#endregion
//#region src/components/DropdownMenu/DropdownMenuRadioGroup/DropdownMenuRadioItem.tsx
const DropdownMenuRadioItem = ({ className, children, closeOnSelect, onSelect, indicatorAlign = "start", indicator = "radio", icon, iconClassName, isLoading = false, ...props }) => {
	const isAtStart = indicatorAlign === "start";
	const indicatorEle = isLoading ? /* @__PURE__ */ jsx(ItemIcon, {
		className: iconClassName,
		children: /* @__PURE__ */ jsx(Loader2, { className: "tw:animate-spin" })
	}) : /* @__PURE__ */ jsx(DropdownMenuItemIndicator, { variant: indicator });
	const handleSelect = (event) => {
		if (!closeOnSelect) event.preventDefault();
		onSelect?.(event);
	};
	return /* @__PURE__ */ jsxs(DropdownMenuPrimitive.RadioItem, {
		className: cn(itemVariants, "tw:data-[state=checked]:cursor-default", {
			"tw:group-data-[variant=default]/dropdown-menu-content:data-[state=checked]:bg-secondary": indicator === "none",
			"tw:group-data-[variant=argo]/dropdown-menu-content:data-[state=checked]:bg-[var(--selected,color-mix(in_oklab,rgb(var(--primary))_10%,transparent))]": indicator !== "radio"
		}, className),
		onSelect: handleSelect,
		...props,
		children: [
			isAtStart && indicatorEle,
			icon && /* @__PURE__ */ jsx(ItemIcon, {
				className: iconClassName,
				children: icon
			}),
			/* @__PURE__ */ jsx("span", {
				className: "tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2",
				children
			}),
			!isAtStart && indicatorEle
		]
	});
};
//#endregion
//#region src/components/DropdownMenu/DropdownMenuSeparator.tsx
const DropdownMenuSeparator = ({ className, ...props }) => /* @__PURE__ */ jsx(DropdownMenuPrimitive.Separator, {
	className: cn("tw:relative", "tw:z-10", "tw:flex", "tw:items-center", "tw:justify-center", "tw:w-full", "tw:before:content-[\"\"]", "tw:before:w-full", "tw:before:h-px", "tw:group-data-[variant=default]/dropdown-menu-content:py-2", "tw:group-data-[variant=default]/dropdown-menu-content:before:bg-border", "tw:group-data-[variant=argo]/dropdown-menu-content:py-1", "tw:group-data-[variant=argo]/dropdown-menu-content:before:bg-border", className),
	...props
});
//#endregion
//#region src/components/DropdownMenu/DropdownMenuSub/DropdownMenuSub.tsx
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
//#endregion
//#region src/components/DropdownMenu/DropdownMenuSub/DropdownMenuSubContent.tsx
const DropdownMenuSubContent = ({ className, ...props }) => {
	const { variant } = useDropdownMenuContext();
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.SubContent, {
		"data-variant": variant,
		className: cn(contentVariants, "tw:z-[1041]", "tw:p-2", className),
		...props
	}) });
};
//#endregion
//#region src/components/DropdownMenu/DropdownMenuSub/DropdownMenuSubTrigger.tsx
const DropdownMenuSubTrigger = ({ className, children, icon, ...props }) => {
	return /* @__PURE__ */ jsxs(DropdownMenuPrimitive.SubTrigger, {
		className: cn(itemVariants, className),
		...props,
		children: [
			icon && /* @__PURE__ */ jsx(ItemIcon, { children: icon }),
			children,
			/* @__PURE__ */ jsx(ChevronRight, { className: "tw:ml-auto tw:size-5" })
		]
	});
};
//#endregion
//#region src/components/DropdownMenu/DropdownMenuTrigger.tsx
const DropdownMenuTrigger = ({ ref, className, ...props }) => /* @__PURE__ */ jsx(Trigger, {
	...props,
	ref,
	"data-slot": "dropdown-menu-trigger",
	className: cn("tw:group/dropdown-menu-trigger", className)
});
//#endregion
//#region src/components/DropdownMenu/DropdownMenu.tsx
const DropdownMenu = ({ modal = false, ...props }) => {
	const effectiveVariant = useArgoDesign() ? "argo" : "default";
	const contextValue = useMemo(() => ({ variant: effectiveVariant }), [effectiveVariant]);
	return /* @__PURE__ */ jsx(DropdownMenuContext, {
		value: contextValue,
		children: /* @__PURE__ */ jsx(Root$1, {
			...props,
			modal
		})
	});
};
/**
* Main Layout
*/
DropdownMenu.Trigger = DropdownMenuTrigger;
DropdownMenu.Content = DropdownMenuContent;
DropdownMenu.Group = Group;
/**
* Sub Layout
*/
DropdownMenu.Sub = DropdownMenuSub;
DropdownMenu.SubContent = DropdownMenuSubContent;
DropdownMenu.SubTrigger = DropdownMenuSubTrigger;
/**
* Items can be used either Sub ar Main Layout
*/
DropdownMenu.Separator = DropdownMenuSeparator;
DropdownMenu.Label = DropdownMenuLabel;
DropdownMenu.Item = DropdownMenuItem;
DropdownMenu.CheckboxItem = DropdownMenuCheckboxItem;
DropdownMenu.RadioGroup = DropdownMenuRadioGroup;
DropdownMenu.RadioItem = DropdownMenuRadioItem;
DropdownMenu.ItemDescription = DropdownMenuItemDescription;
//#endregion
export { DropdownMenu };

//# sourceMappingURL=DropdownMenu-DtRYcKEx.js.map